import { EditIcon } from '@chakra-ui/icons'
import { Flex, IconButton, Text } from '@chakra-ui/react'
import { DropdownOptionItem } from '@hb/shared'
import { useMemo } from 'react'
import { ActionButton, SolidActionButton } from '../../../../Buttons/ActionButton'
import { useFormWizard } from '../../../FormWizard/context'

export const OptionSelectButton = ({
  text,
  loading,
  onClick,
  selected,
  subText,
}: {
  text: string
  subText?: string
  loading?: boolean
  onClick?: () => void
  selected?: boolean
}) => {
  const Component = selected ? ActionButton : SolidActionButton
  return (
    <Component
      isLoading={loading}
      pointerEvents={onClick ? 'auto' : 'none'}
      width="100%"
      height="auto"
      minH={8}
      colorScheme={selected ? 'gray' : undefined}
      _hover={{ bg: selected ? 'green.50' : 'green.400' }}
      py={2}
      gap={1}
      onClick={onClick}
      flexFlow="column"
      whiteSpace="pre-wrap">
      <Text textAlign="center" position="relative" top="1px" fontWeight={600} lineHeight={1.2}>
        {text}
      </Text>
      {subText ? (
        <Text
          position="relative"
          top="1px"
          textAlign="left"
          lineHeight={1.2}
          fontWeight={400}
          fontSize="md">
          {subText}
        </Text>
      ) : null}
    </Component>
  )
}

export const OptionSelectButtons = <T extends string>({
  options,
  onSelectOption,
  loadingOption,
}: {
  options: DropdownOptionItem<T>[]
  onSelectOption: (option: T) => Promise<void>
  loadingOption: T | null
}) => (
  <Flex gap={1.5} flexFlow="column" flex={1} minW={0}>
    {options.map(option => (
      <OptionSelectButton
        key={option.id}
        loading={loadingOption === option.id}
        text={option.text}
        onClick={() => onSelectOption(option.id)}
      />
    ))}
  </Flex>
)

export const SelectedOptionView = <T extends string>({
  selected,
  options,
  loading,
  onEditClick,
  subText,
}: {
  selected: T | null | undefined
  options: DropdownOptionItem<T>[]
  onEditClick: () => void
  loading?: boolean
  subText?: string
}) => {
  const { readOnly } = useFormWizard()
  const selectedOption = useMemo(() => options.find(o => o.id === selected), [options, selected])

  return (
    <Flex gap={1.5} align="center" width="100%">
      <Flex flex={1} minW={0}>
        <OptionSelectButton
          selected
          loading={loading}
          text={selectedOption?.text ?? ''}
          subText={subText}
        />
      </Flex>
      {!readOnly ? (
        <IconButton
          borderRadius="full"
          onClick={onEditClick}
          icon={<EditIcon />}
          aria-label="Edit"
        />
      ) : null}
    </Flex>
  )
}
